import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react"
import { navigate } from "gatsby"
import { motion, stagger, useAnimate, useReducedMotion } from "motion/react"
import "./curtains.css"

/**
 * A shutter page transition, modelled on the Motion Curtains "shutter" effect:
 * interleaved columns that close in from alternating edges, hiding the route
 * change, then continue past in the same direction to reveal the new page.
 */
const SLAT_SIZE = 120
const COVER_DURATION = 0.45
const REVEAL_DURATION = 0.5
const SLAT_STAGGER = 0.035

const CurtainsContext = createContext(null)

const nextPaint = () =>
  new Promise((resolve) =>
    requestAnimationFrame(() => requestAnimationFrame(resolve))
  )

export const CurtainsProvider = ({ children }) => {
  const [scope, animate] = useAnimate()
  const [slats, setSlats] = useState(0)
  const [isPending, setIsPending] = useState(false)
  const prefersReducedMotion = useReducedMotion()
  const runningRef = useRef(false)

  useEffect(() => {
    const measure = () => setSlats(Math.ceil(window.innerWidth / SLAT_SIZE) + 1)

    measure()
    window.addEventListener("resize", measure)
    return () => window.removeEventListener("resize", measure)
  }, [])

  /**
   * Cover the view, run the update while it's hidden, then reveal. Mirrors the
   * shape of Motion's `curtains(callback)` so call sites read the same way.
   */
  const curtains = useCallback(
    async (callback) => {
      /**
       * A hidden tab suspends requestAnimationFrame, which would leave the
       * awaited cover animation - and so the navigation - stalled. Skip
       * straight to the update in that case.
       */
      if (
        prefersReducedMotion ||
        !slats ||
        runningRef.current ||
        document.hidden
      ) {
        await callback()
        return
      }

      runningRef.current = true
      setIsPending(true)

      const options = {
        duration: COVER_DURATION,
        ease: [0.65, 0, 0.35, 1],
        delay: stagger(SLAT_STAGGER),
      }

      await Promise.all([
        animate(".shutter-slat-down", { y: "0%" }, options),
        animate(".shutter-slat-up", { y: "0%" }, options),
      ])

      await callback()
      await nextPaint()

      const revealOptions = {
        duration: REVEAL_DURATION,
        ease: [0.65, 0, 0.35, 1],
        delay: stagger(SLAT_STAGGER),
      }

      /**
       * "normal" direction mode: each column carries on the way it came in
       * rather than retreating back to its own edge.
       */
      await Promise.all([
        animate(".shutter-slat-down", { y: "100%" }, revealOptions),
        animate(".shutter-slat-up", { y: "-100%" }, revealOptions),
      ])

      // Park the columns back at their starting edges for the next transition.
      animate(".shutter-slat-down", { y: "-100%" }, { duration: 0 })
      animate(".shutter-slat-up", { y: "100%" }, { duration: 0 })

      setIsPending(false)
      runningRef.current = false
    },
    [animate, prefersReducedMotion, slats]
  )

  return (
    <CurtainsContext.Provider value={{ curtains, isPending }}>
      {children}
      <div
        className="shutter"
        ref={scope}
        aria-hidden="true"
        style={{ pointerEvents: isPending ? "auto" : "none" }}
      >
        {Array.from({ length: slats }, (_, index) => {
          const fromTop = index % 2 === 0
          return (
            <motion.div
              key={index}
              className={`shutter-slat ${
                fromTop ? "shutter-slat-down" : "shutter-slat-up"
              }`}
              initial={{ y: fromTop ? "-100%" : "100%" }}
            />
          )
        })}
      </div>
    </CurtainsContext.Provider>
  )
}

export const useCurtains = () => {
  const context = useContext(CurtainsContext)
  if (!context) {
    throw new Error("useCurtains must be used within a CurtainsProvider")
  }
  return context
}

/**
 * Drop-in replacement for Gatsby's `navigate` that runs the shutter first.
 */
export const useCurtainsNavigate = () => {
  const { curtains } = useCurtains()
  return useCallback((to) => curtains(() => navigate(to)), [curtains])
}
